import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { ROLE_LABEL_KEYS, TIER_LABEL_KEYS } from "../types";
import type { AdminSearchUserRow } from "../types";

const ROLE_BADGE_COLORS: Record<string, string> = {
  ADMIN: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  MODERATOR:
    "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  ORG_ADMIN:
    "bg-secondary/10 text-secondary",
  USER: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

const TIER_BADGE_COLORS: Record<string, string> = {
  FREE: "bg-muted text-muted-foreground",
  PREMIUM:
    "bg-koins/15 text-koins",
  ENTERPRISE:
    "bg-secondary/10 text-secondary",
};

interface UserSearchResultsProps {
  users: AdminSearchUserRow[];
  totalCount: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  selectedUserId: string | null;
  onSelectUser: (user: AdminSearchUserRow) => void;
}

export function UserSearchResults({
  users,
  totalCount,
  isLoading,
  page,
  pageSize,
  onPageChange,
  selectedUserId,
  onSelectUser,
}: UserSearchResultsProps) {
  const { t } = useLocale();
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalCount);

  if (isLoading && users.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-lg border border-border bg-card"
          />
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-12 text-center">
        <span className="text-3xl">👤</span>
        <p className="mt-2 text-sm font-medium text-foreground">
          {t("users.results.emptyTitle")}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("users.results.emptyHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("users.field.user")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("users.field.role")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("users.field.tier")}
              </th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                {t("users.field.balance")}
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                {t("users.field.lastActive")}
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr
                key={user._id}
                onClick={() => onSelectUser(user)}
                className={`cursor-pointer border-b border-border last:border-b-0 transition-colors ${
                  selectedUserId === user._id
                    ? "bg-primary/10"
                    : "hover:bg-muted/30"
                }`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                      {user.avatar ? (
                        <img
                          src={user.avatar}
                          alt=""
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        `${user.firstName[0]}${user.lastName[0]}`
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">
                        {user.firstName} {user.lastName}
                        {!user.isActive && (
                          <span className="ml-1.5 text-xs text-destructive">
                            {t("users.results.deactivated")}
                          </span>
                        )}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      ROLE_BADGE_COLORS[user.role] ?? ROLE_BADGE_COLORS.USER
                    }`}
                  >
                    {t(ROLE_LABEL_KEYS[user.role] ?? user.role)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      TIER_BADGE_COLORS[user.tier] ?? TIER_BADGE_COLORS.FREE
                    }`}
                  >
                    {t(TIER_LABEL_KEYS[user.tier] ?? user.tier)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-mono text-sm font-medium text-koins">
                    {user.koinBalance.toLocaleString()}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {user.lastSeenAt
                    ? format(new Date(user.lastSeenAt), "MMM d, yyyy")
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalCount > pageSize && (
        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <span className="text-xs text-muted-foreground">
            {t("common.showingRange", {
              start: startRow,
              end: endRow,
              total: totalCount,
            })}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 0}
              onClick={() => onPageChange(page - 1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 text-xs text-muted-foreground">
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => onPageChange(page + 1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
